import { useEffect } from "react";
import { getUserId } from "../utils/auth";

interface PreservedState {
  route: string;
  scrollPosition: { x: number; y: number };
  timestamp: string;
  expiresAt: string;
}

function buildStateKey(userId: string): string {
  return `seweb:state:${userId}`;
}

/**
 * Preserves user navigation state (route, scroll position) when
 * maintenance:autosave event fires. State is saved to localStorage
 * with a 24h TTL for later restoration (Story 3.7).
 */
export function useStatePreservation(): void {
  useEffect(() => {
    const handleAutoSave = () => {
      const userId = getUserId();
      if (!userId) return;

      const now = new Date();
      const state: PreservedState = {
        route: window.location.pathname + window.location.search,
        scrollPosition: { x: window.scrollX, y: window.scrollY },
        timestamp: now.toISOString(),
        expiresAt: new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString(),
      };

      try {
        localStorage.setItem(buildStateKey(userId), JSON.stringify(state));
      } catch {
        // localStorage full or unavailable — silent fail
      }
    };

    window.addEventListener("maintenance:autosave", handleAutoSave);
    return () => window.removeEventListener("maintenance:autosave", handleAutoSave);
  }, []);
}
